import { useEffect, useRef, useState } from "react";
import { Input } from "@/ui/input";
import { Textarea } from "@/ui/textarea";
import { useAcoes, useEstado } from "../estado.jsx";
import { cn } from "@/lib/utils";
import { ICONES, Icone } from "../visual.jsx";
import { CampoDeData } from "./CampoDeData.tsx";
import { Gravou, Seletor } from "./comuns.jsx";
import { CAMPO } from "./pele.ts";

/**
 * Um campo da ficha — SEMPRE ABERTO, gravando sozinho.
 *
 * Três gerações neste arquivo, e cada uma morreu pelo mesmo motivo: o dado
 * estava a um clique de distância. Primeiro o rótulo era a CHAVE do banco
 * (`funcao_cliente`) com o valor em monoespaçado à direita, só leitura — corrigir
 * a admissão que a extração leu torta custava abrir outra tela. Depois veio o
 * editar-no-lugar: clicar abria o campo, Enter gravava, Esc desistia. Melhor, e
 * ainda assim um pedágio por campo — e pior, invisível: um texto cinza sobre
 * fundo transparente não anuncia que aceita digitação, então a ficha parecia
 * relatório e quem atende ia escrever na trilha.
 *
 * Agora todo campo JÁ É o controle dele: caixa com borda, do tipo certo (data
 * abre o calendário do aparelho, seleção abre a lista do escritório, texto longo
 * abre a área que cresce). Digitou, saiu do campo, gravou — e a gravação também
 * acontece sozinha um segundo depois da última tecla, para quem sai da tela sem
 * clicar fora. O recibo é o `Gravou` verde na própria linha: aviso por campo
 * corrigido soltaria uma torrada a cada tecla de quem está arrumando a ficha
 * inteira.
 *
 * O que NÃO mudou, e é o que faz isto ser seguro: o rascunho só renasce quando o
 * CASO muda. O evento do servidor chega no meio da digitação — é a regra de
 * "recarregue a trilha, nunca o rascunho", agora valendo para vinte campos.
 */

/** Quanto tempo depois da última tecla o campo grava sozinho. */
const GRAVA_DEPOIS_DE = 900;

export default function CampoDaFicha({ chave, valor, campo, primeiro, falta = false }) {
  const { listas, casoId, caso } = useEstado();
  const { salvarCampo } = useAcoes();

  const texto = valor == null ? "" : String(valor);
  const tipo = campo?.tipo ?? (texto.length > 90 ? "texto_longo" : "texto");
  const rotulo = campo?.rotulo ?? chave.replaceAll("_", " ");
  const opcoes = tipo === "selecao" ? (listas?.[campo?.lista] ?? []) : null;
  const longo = tipo === "texto_longo" || texto.length > 90;
  /* PELA IA: o último que mexeu neste campo foi a máquina, e o valor na tela é
     o que ela gravou. A trilha já diz isso (evento `campo`, autor
     "IA (automático)", a mais nova primeiro); desenhar é o "quem abrir amanhã
     vê que não foi gente". Some sozinho quando uma pessoa grava por cima, porque
     aí o último evento é dela. */
  const mexida = (caso?.trilha ?? []).find((e) => e.tipo === "campo" && e.texto === chave);
  const pelaIA = texto !== "" && /automático/.test(mexida?.autor ?? "") && String(mexida?.para ?? "") === texto;

  const [rascunho, setRascunho] = useState(texto);
  const relogio = useRef(null);
  const ultimo = useRef(texto);

  /* Só na troca de CASO (e de campo): reler o caso não pode apagar o que está na
     mão de quem digita. */
  useEffect(() => {
    setRascunho(texto);
    ultimo.current = texto;
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- de propósito */
  }, [casoId, chave]);

  useEffect(() => () => clearTimeout(relogio.current), []);

  const gravar = (bruto) => {
    const novo = String(bruto ?? "").trim();
    if (novo === ultimo.current.trim()) return;
    ultimo.current = novo;
    salvarCampo(chave, novo);
  };

  const escrever = (bruto) => {
    setRascunho(bruto);
    clearTimeout(relogio.current);
    relogio.current = setTimeout(() => gravar(bruto), GRAVA_DEPOIS_DE);
  };

  const aoSair = (e) => {
    clearTimeout(relogio.current);
    gravar(e.target.value);
  };

  /* Enter grava no campo de uma linha; no texto longo ele é quebra de linha, e
     quem grava é o relógio ou sair do campo. */
  const teclas = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      clearTimeout(relogio.current);
      setRascunho(ultimo.current);
      e.currentTarget.blur();
      return;
    }
    if (e.key === "Enter" && tipo !== "texto_longo") {
      e.preventDefault();
      clearTimeout(relogio.current);
      gravar(e.currentTarget.value);
    }
  };

  /* A pele de campo da casa (36, raio 10, borda 3:1, foco em borda terra) em
     TODO tipo: o texto media 31 e a seleção 45, e a ficha parecia ter três
     controles diferentes. */
  const pele = cn(
    CAMPO,
    "placeholder:italic",
    pelaIA && "border-dashed border-verde/55 bg-[color-mix(in_oklab,var(--color-verde)_5%,var(--color-card))]",
  );

  /* Seleção: o próprio seletor é o campo, e escolher grava. Limpar é o ✕ ao
     lado, que só existe quando há o que limpar — o `Select` do registro recusa
     item com `value=""` (é o valor dele para "nada escolhido"). */
  if (opcoes?.length)
    return (
      <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA}>
        <span className="flex items-center gap-1">
          <Seletor
            rotulo={texto ? rotulo : `Escolher ${rotulo.toLowerCase()}`}
            comoCampo
            largura="100%"
            valor={texto}
            aoEscolher={(v) => v !== texto && salvarCampo(chave, v)}
            opcoes={opcoes.map((o) => ({ id: o, rotulo: o }))}
          />
          {texto ? (
            <button
              type="button"
              onClick={() => salvarCampo(chave, "")}
              data-dica={`Limpar ${rotulo.toLowerCase()}`}
              aria-label={`Limpar ${rotulo.toLowerCase()}`}
              /* No dedo não há hover que o revele: lá ele fica à vista. */
              className="alvo flex size-mini flex-none cursor-pointer items-center justify-center rounded-controle border-none bg-transparent p-0 text-tinta-fraca opacity-0 transition-opacity duration-[var(--t-curta)] group-hover/linha:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hover:text-destructive"
            >
              <Icone de={ICONES.fechar} tamanho={14} />
            </button>
          ) : null}
        </span>
      </Linha>
    );

  if (longo)
    return (
      <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA} emCima>
        <Textarea
          value={rascunho}
          onChange={(e) => escrever(e.target.value)}
          onBlur={aoSair}
          onKeyDown={teclas}
          aria-label={rotulo}
          placeholder={`${rotulo} — grava sozinho`}
          className={cn(pele, "h-auto min-h-[76px] py-2 leading-[1.5] max-[759px]:h-auto")}
        />
      </Linha>
    );

  if (tipo === "data")
    return (
      <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA}>
        <CampoDeData
          rotulo={rotulo}
          valor={rascunho.slice(0, 10)}
          dica="preencher"
          aoMudar={(v) => {
            clearTimeout(relogio.current);
            setRascunho(v);
            gravar(v);
          }}
          /* O gatilho da data é um botão, mas lê como campo: o foco é a borda
             terra, sem o anel dos botões por cima. */
          className={cn(pele, "focus-visible:outline-none!")}
        />
      </Linha>
    );

  return (
    <Linha rotulo={rotulo} primeiro={primeiro} chave={chave} falta={falta} pelaIA={pelaIA}>
      <Input
        type={tipo === "numero" ? "number" : tipo === "telefone" ? "tel" : "text"}
        value={rascunho}
        onChange={(e) => escrever(e.target.value)}
        onBlur={aoSair}
        onKeyDown={teclas}
        aria-label={rotulo}
        placeholder="preencher"
        className={cn(pele, tipo === "numero" && "font-mono")}
      />
    </Linha>
  );
}

/**
 * A linha: rótulo à esquerda, campo à direita.
 *
 * O lápis do `hover` morreu com o clique-para-editar: não há mais o que abrir, e
 * um ícone de editar ao lado de um campo já aberto é promessa repetida.
 */
function Linha({ rotulo, chave, children, primeiro, emCima = false, falta = false, pelaIA = false }) {
  return (
    <Gravou
      chave={`campo:${chave}`}
      /* `falta`: a peça imprime este campo e ele está em branco. A borda terra
         é a marca que a frase do alto da ficha promete ("estão marcados
         abaixo") — sem ela a frase mentia. */
      classe={`group/linha flex gap-2 py-[7px] ${emCima ? "flex-col" : "items-center"} ${
        primeiro ? "" : "border-t border-t-foreground/8"
      } ${falta ? "-ml-3 border-l-[3px] border-l-terra pl-[9px]" : ""}`}
    >
      {/* O rótulo QUEBRA em vez de cortar: os do esquema são frases inteiras
          ("Data do primeiro contato", "Empresa reclamada"), e reticência em
          "Data do primeiro…" faz duas linhas da ficha parecerem o mesmo campo. */}
      <span
        className={`min-w-0 text-apoio leading-[1.3] text-balance text-tinta-fraca ${emCima ? "" : "flex-none basis-[40%]"}`}
      >
        {rotulo}
        {falta ? <span className="block font-semibold text-terra-escura">falta na peça</span> : null}
        {pelaIA ? <span className="block font-semibold text-verde">pela IA</span> : null}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </Gravou>
  );
}
